import { useState } from 'react';
import { Label } from './label';
import { labelClassName } from './formStyles';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

// Sentinel used by the optional "empty" option so a real empty selection ("") can round-trip
// through Radix Select (which cannot use "" as an item value).
const EMPTY_VALUE = "__EMPTY__";

// Value shown by the trigger: the current value when set, else the empty sentinel when the field
// allows clearing, else "". Extracted so the component body stays flat (Sonar S3776).
function resolveSelectValue(value, allowEmpty) {
  if (value && value !== "") return value;
  if (allowEmpty) return EMPTY_VALUE;
  return "";
}

// Two selection behaviours, bound at the call site instead of branching on the `allowEmpty`
// control flag inside one handler (Sonar S2301). Each is a factory over `onValueChange`.
const emitTruthyValue = (onValueChange) => (newValue) => {
  if (newValue) onValueChange(newValue);
};

const emitValueAllowingEmpty = (onValueChange) => (newValue) => {
  if (newValue === EMPTY_VALUE) {
    onValueChange("");
  } else if (newValue) {
    onValueChange(newValue);
  }
};

// Renders the body of the dropdown (loading / no-options / the option items). Kept at module scope
// so its branching does not count toward the component's cognitive complexity (Sonar S3776).
function renderDropdownItems({ loading, options, dataTestId }) {
  const suffixId = (suffix) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  if (loading) {
    return (
      <div className="px-2 py-1.5 text-sm text-fg-muted" data-testid={suffixId('loading')}>
        Loading...
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="px-2 py-1.5 text-sm text-fg-muted" data-testid={suffixId('empty-options')}>
        No options available.
      </div>
    );
  }

  return options.map((option) => (
    <SelectItem
      key={option.value || option.id}
      value={option.value?.toString() || option.id?.toString()}
      dataTestId={suffixId(`option-${option.value || option.id}`)}
    >
      {option.label || option.name}
    </SelectItem>
  ));
}

export function DropdownOptions({
  id,
  label,
  value,
  onValueChange,
  options = [],
  loading = false,
  disabled = false,
  placeholder = "Select an option",
  className = "",
  triggerClassName = "",
  error,
  required = false,
  allowEmpty = false,
  emptyLabel = "No selection",
  // With `allowEmpty`, an empty value normally shows the empty option's label in the trigger. Set
  // this to keep showing `placeholder` instead (e.g. "Select region (optional)") — the empty option
  // stays in the list as the way to clear the field.
  placeholderWhenEmpty = false,
  maxHeight = "200px",
  // Classes for the error line. Defaults to the size every existing dropdown has; a form whose other
  // fields use errorTextClassName passes that, so its dropdown errors match its input errors.
  errorClassName = "text-danger-fg text-xs mt-1",
  dataTestId
}) {
  const [isOpen, setIsOpen] = useState(false);

  const suffixId = (suffix) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  // Announced on the trigger rather than only drawn as an asterisk, so the requirement and the error
  // reach a screen reader too. Callers that render their own <Label> still get this by passing
  // `required`, so the field is marked the same way regardless of who draws the label.
  const errorId = error && id ? `${id}-error` : undefined;

  const handleValueChange = (allowEmpty ? emitValueAllowingEmpty : emitTruthyValue)(onValueChange);

  // Default trigger styles - can be overridden via triggerClassName.
  // Token-backed rather than hex, so the trigger follows light/dark like the popover it opens
  // (SelectContent is already variable-driven). The light values are the ones these hexes had.
  const defaultTriggerClass = `w-full h-11 px-3 border border-line-input rounded-lg bg-surface-filter data-[placeholder]:text-fg-placeholder-select text-sm ${
    disabled ? 'text-fg-disabled opacity-60' : 'text-fg'
  }`;

  const selectValue = resolveSelectValue(value, allowEmpty && !placeholderWhenEmpty);

  const optionsContent = renderDropdownItems({ loading, options, dataTestId });

  return (
    <div className={`space-y-2 ${className}`} data-testid={dataTestId}>
      {label && (
        <Label htmlFor={id} className={labelClassName} dataTestId={suffixId('label')}>
          {label}
          {required && <span className="text-required-mark ml-1">*</span>}
        </Label>
      )}

      <Select
        value={selectValue}
        onValueChange={handleValueChange}
        disabled={disabled || loading}
        open={isOpen}
        onOpenChange={setIsOpen}
        dataTestId={dataTestId}
      >
        <SelectTrigger
          id={id}
          className={triggerClassName || defaultTriggerClass}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          dataTestId={suffixId('trigger')}
        >
          <SelectValue placeholder={loading ? "Loading..." : placeholder} dataTestId={suffixId('value')} />
        </SelectTrigger>

        <SelectContent
          className="max-h-60 overflow-y-auto"
          style={{ maxHeight }}
          dataTestId={suffixId('content')}
        >
          {allowEmpty && (
            <SelectItem value={EMPTY_VALUE} dataTestId={suffixId('empty-option')}>
              {emptyLabel}
            </SelectItem>
          )}

          {optionsContent}
        </SelectContent>
      </Select>

      {error && (
        <p id={errorId} className={errorClassName} data-testid={suffixId('error')}>{error}</p>
      )}
    </div>
  );
}
